// src/components/OrderItems.js
import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Tooltip,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
    buildMintsoftDisplayGroups,
    isMintsoftDisplayGroupConfirmed,
} from '../utils/mintsoftBundles';

const OrderItems = ({
    order,
    orderProducts,
    bundleDetails = {},
    lineImagesMap,
    confirmedLineIds,
    setConfirmedLineIds,
    isPersonalised,
    orderSource, // "mintsoft" or "kornitx"
}) => {
    const [zoomImage, setZoomImage] = useState(null);

    // For Mintsoft orders, allow toggling confirmation.
    // For KornitX orders, individual toggling is disabled.
    const handleToggleConfirm = (itemIds) => {
        if (orderSource === 'mintsoft') {
            const ids = Array.isArray(itemIds) ? itemIds : [itemIds];
            const allConfirmed = ids.every((id) => confirmedLineIds.includes(id));

            setConfirmedLineIds((prev) =>
                allConfirmed
                    ? prev.filter((id) => !ids.includes(id))
                    : Array.from(new Set([...prev, ...ids]))
            );
        }
    };

    // Determine which list of items to use:
    const itemsList =
        orderSource === 'kornitx'
            ? (order.items?.items || [])
            : buildMintsoftDisplayGroups(order.OrderItems || []);

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 1.5,
                    alignContent: 'start',
                }}
            >
                {itemsList.map((displayItem) => {
                    // For Mintsoft orders, use product details and personalised images.
                    // For KornitX orders, use item fields directly.
                    let finalImage = '';
                    let quantity = 0;
                    let sku = '';
                    let description = '';
                    let itemId = '';
                    let artwork_barcode = '';
                    let cardKey = '';
                    let itemIds = [];
                    let isBundle = false;
                    let bundleComponents = [];
                    let isPersonalisedDisplayItem = false;

                    if (orderSource === 'mintsoft') {
                        isBundle = displayItem.type === 'bundle';
                        itemIds = displayItem.items.map((item) => item.ID);
                        cardKey = displayItem.key;
                        isPersonalisedDisplayItem = itemIds.some((id) => isPersonalised?.(id));

                        if (isBundle) {
                            const bundleProductId = displayItem.bundleInfo.bundleProductId;
                            const bundleMeta = bundleDetails[bundleProductId] || {};
                            const bundleProduct = bundleMeta.product || {};
                            const bundle = bundleMeta.bundle || {};
                            const personalisedImageData = displayItem.items
                                .map((componentItem) => lineImagesMap[componentItem.ID])
                                .find((imageData) => imageData?.thumbnailUrl);

                            finalImage = personalisedImageData?.thumbnailUrl || bundleProduct.ImageURL || bundle.ImageURL || '';
                            quantity = displayItem.bundleInfo.bundleQuantity;
                            sku = bundleProduct.SKU || bundle.SKU || `Bundle ${bundleProductId}`;
                            description = bundleProduct.Name || bundle.Name || 'Bundle';
                            bundleComponents = displayItem.items.map((componentItem) => {
                                const componentProduct = orderProducts[componentItem.ProductId] || {};

                                return {
                                    id: componentItem.ID,
                                    sku: componentProduct.SKU || componentItem.SKU || '',
                                    name: componentProduct.Name || componentProduct.Description || componentItem.Details || '',
                                    quantity: componentItem.Quantity || 0,
                                };
                            });
                        } else {
                            const item = displayItem.item;
                            const productId = item.ProductId;
                            const prod = orderProducts[productId] || {};
                            const imageData = lineImagesMap[item.ID];
                            finalImage = imageData?.thumbnailUrl || prod.ImageURL;
                            quantity = item.Quantity;
                            sku = prod.SKU || item.SKU;
                            description = prod.Name || prod.Description || 'N/A';
                            itemId = imageData?.kxItemId || '';
                            artwork_barcode = imageData?.artwork_barcode || '';
                        }
                    } else {
                        // KornitX order item structure:
                        // item contains: id, description, mapped_sku, quantity, artwork (array)
                        const item = displayItem;
                        cardKey = item.id;
                        itemIds = [item.id];
                        finalImage = item.thumbnail?.url || '';
                        quantity = item.quantity;
                        sku = item.mapped_sku || item.sku || '';
                        description = item.description;
                        itemId = item.id;
                        artwork_barcode = item.artwork_barcode;
                    }

                    // Use item.ID for Mintsoft; for KornitX, use item.id
                    const isConfirmed =
                        orderSource === 'mintsoft'
                            ? isMintsoftDisplayGroupConfirmed(displayItem, confirmedLineIds)
                            : confirmedLineIds.includes(displayItem.id);
                    const bundleTooltipTitle = isBundle && bundleComponents.length > 0 ? (
                        <Box sx={{ p: 1, maxWidth: 420 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75, color: 'text.primary' }}>
                                {sku || 'Bundle'} contents
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {bundleComponents.map((component) => (
                                    <Box
                                        key={component.id}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '42px 92px minmax(0, 1fr)',
                                            gap: 0.75,
                                            alignItems: 'start',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800 }}>
                                            x{component.quantity}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 800 }}>
                                            {component.sku || 'No SKU'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.35 }}>
                                            {component.name || 'N/A'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : '';

                    const card = (
                        <Card
                            sx={{
                                position: 'relative',
                                height: '100%',
                                borderRadius: 2.5,
                                cursor: orderSource === 'mintsoft' ? 'pointer' : 'default',
                                border: '1px solid',
                                borderColor: isConfirmed ? 'success.light' : 'divider',
                                boxShadow: isConfirmed
                                    ? '0 10px 24px rgba(46, 125, 50, 0.12)'
                                    : '0 10px 24px rgba(15, 23, 42, 0.06)',
                                backgroundColor: isConfirmed ? 'rgba(46, 125, 50, 0.04)' : '#fff',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: orderSource === 'mintsoft' ? 'translateY(-2px)' : 'none',
                                    boxShadow: isConfirmed
                                        ? '0 14px 28px rgba(46, 125, 50, 0.16)'
                                        : '0 14px 28px rgba(15, 23, 42, 0.1)',
                                },
                            }}
                            onClick={() =>
                                handleToggleConfirm(itemIds)
                            }
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    aspectRatio: '4 / 3',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.100',
                                }}
                            >
                                {finalImage ? (
                                    <Box
                                        component="img"
                                        src={finalImage}
                                        alt={description}
                                        sx={{
                                            height: '100%',
                                            width: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                        onClick={(e) => e.stopPropagation()} // still allow zoom functionality
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'grey.200',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Typography variant="caption">No Image</Typography>
                                    </Box>
                                )}

                                {/* Top-left quantity tag */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        px: 1,
                                        py: 0.35,
                                        fontWeight: 'bold',
                                        borderRadius: 999,
                                        fontSize: '0.75rem',
                                        boxShadow: '0 6px 14px rgba(25, 118, 210, 0.25)',
                                    }}
                                >
                                    Qty: {quantity}
                                </Box>

                                {isConfirmed && (
                                    <Chip
                                        icon={<CheckCircleRoundedIcon />}
                                        label="Confirmed"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: 'rgba(255,255,255,0.92)',
                                            borderColor: 'success.main',
                                            color: 'success.main',
                                            '& .MuiChip-icon': { color: 'success.main' },
                                            '& .MuiChip-label': { fontWeight: 700 },
                                        }}
                                    />
                                )}

                                {isBundle && !isConfirmed && (
                                    <Chip
                                        label="Bundle"
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: 'rgba(255,255,255,0.92)',
                                            borderColor: 'secondary.main',
                                            color: 'secondary.main',
                                            '& .MuiChip-label': { fontWeight: 700 },
                                        }}
                                    />
                                )}

                                {/* Zoom icon */}
                                {finalImage && (
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setZoomImage(finalImage);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(15, 23, 42, 0.7)',
                                            borderRadius: '50%',
                                            padding: 0.5,
                                            cursor: 'pointer',
                                            zIndex: 2,
                                        }}
                                    >
                                        <ZoomInIcon sx={{ color: 'white' }} />
                                    </Box>
                                )}
                            </Box>

                            <CardContent
                                sx={{
                                    p: 1.25,
                                    '&:last-child': { pb: 1.25 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        letterSpacing: 0.25,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {sku || 'No SKU'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        fontWeight: 600,
                                        lineHeight: 1.35,
                                        minHeight: 38,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {description || 'N/A'}
                                </Typography>

                                {!finalImage && isPersonalisedDisplayItem && (
                                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                                        Awaiting Artwork
                                    </Typography>
                                )}

                                {isBundle && bundleComponents.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: 'auto',
                                            pt: 0.75,
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                            Contains {bundleComponents.length} lines
                                        </Typography>
                                        {bundleComponents.slice(0, 4).map((component) => (
                                            <Typography
                                                key={component.id}
                                                variant="caption"
                                                display="block"
                                                sx={{ color: 'text.secondary', wordBreak: 'break-word' }}
                                            >
                                                {component.quantity} x {component.sku || 'No SKU'}
                                                {component.name ? ` - ${component.name}` : ''}
                                            </Typography>
                                        ))}
                                        {bundleComponents.length > 4 && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                +{bundleComponents.length - 4} more lines
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {(!isBundle && ((orderSource === 'mintsoft' && isPersonalisedDisplayItem) || orderSource === 'kornitx')) ? (
                                    <Box sx={{ mt: 'auto', pt: 0.25 }}>
                                        {artwork_barcode && (
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                sx={{ color: 'text.secondary', wordBreak: 'break-all' }}
                                            >
                                                Artwork: {artwork_barcode}
                                            </Typography>
                                        )}
                                        {itemId && (
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                sx={{ color: 'text.secondary', wordBreak: 'break-all' }}
                                            >
                                                Item ID: {itemId}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : null}
                            </CardContent>
                        </Card>
                    );

                    return bundleTooltipTitle ? (
                        <Tooltip
                            key={cardKey}
                            title={bundleTooltipTitle}
                            arrow
                            placement="right-start"
                            enterDelay={300}
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        boxShadow: '0 16px 36px rgba(15, 23, 42, 0.18)',
                                        p: 0,
                                        maxWidth: 460,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: 'background.paper',
                                        '&::before': {
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        },
                                    },
                                },
                            }}
                        >
                            {card}
                        </Tooltip>
                    ) : (
                        <React.Fragment key={cardKey}>{card}</React.Fragment>
                    );
                })}
            </Box>

            {/* Zoom Dialog */}
            <Dialog open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} maxWidth="lg">
                <DialogContent>
                    {zoomImage && (
                        <Box
                            component="img"
                            src={zoomImage}
                            alt="Zoomed Image"
                            sx={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setZoomImage(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OrderItems;
